/**
 * The sea — the one description of the water every side reads.
 *
 * Shared code: the server steps shells against `oceanHeightAt` and the browser
 * both displaces the ocean mesh from these numbers (`ocean-material.ts` builds
 * its GLSL from `WAVES` via `wavesToGlsl`) and samples the same function for
 * buoyancy. All three must agree to the digit, or a hull floats above the
 * surface it is riding and a shell splashes where no wave is — so the formula
 * lives here once and the shader is generated from it rather than written by
 * hand.
 *
 * Sea time is **server time**: the server stamps every snapshot with its `t`
 * and the client offsets its clock to match, so both sample the same surface.
 *
 * Browser-safe and deterministic: no `@nestjs/*`, no Node built-ins, no
 * `Date.now()`, no randomness. Time is an argument.
 */

export interface GerstnerWave {
  /** Direction of travel in the XZ plane, unit-length. */
  dirX: number;
  dirZ: number;
  /** Crest-to-trough half height, metres. */
  amplitude: number;
  /** Wavelength, metres. */
  wavelength: number;
  /** Phase speed, metres per second. */
  speed: number;
  /**
   * Steepness in [0, 1). The horizontal displacement is `steepness * amplitude`,
   * so with the wave number `k = 2π/λ` the surface loops when `Q·A·k > 1` for
   * the summed set. `waveSetIsSafe` checks that.
   */
  steepness: number;
}

function dir(angleDeg: number): { dirX: number; dirZ: number } {
  const a = (angleDeg * Math.PI) / 180;
  return { dirX: Math.cos(a), dirZ: Math.sin(a) };
}

/** Two long swells, two medium waves, two short chop components. */
export const WAVES: readonly GerstnerWave[] = [
  { ...dir(12), amplitude: 1.35, wavelength: 128, speed: 11.0, steepness: 0.5 },
  { ...dir(-34), amplitude: 0.9, wavelength: 86, speed: 8.8, steepness: 0.45 },
  { ...dir(58), amplitude: 0.26, wavelength: 22, speed: 4.8, steepness: 0.55 },
  { ...dir(-71), amplitude: 0.16, wavelength: 13.5, speed: 3.9, steepness: 0.55 },
  { ...dir(103), amplitude: 0.07, wavelength: 5.4, speed: 2.5, steepness: 0.6 },
  { ...dir(-118), amplitude: 0.045, wavelength: 3.2, speed: 2.0, steepness: 0.6 },
];

export interface OceanSample {
  /** Surface height at the sample's *resting* XZ, metres. */
  height: number;
  /** Horizontal displacement of the surface point that started at (x, z). */
  dx: number;
  dz: number;
  /** Unit surface normal. */
  nx: number;
  ny: number;
  nz: number;
}

/**
 * Sums the wave set at resting position (x, z) and time t.
 *
 * Standard Gerstner: the point at (x, z) moves to
 *   (x + Σ Q·A·Dx·cos θ,  Σ A·sin θ,  z + Σ Q·A·Dz·cos θ),  θ = k·(D·p) − ω·t.
 * The normal is the analytic one for that displacement (GPU Gems 1, ch. 1).
 */
export function sampleOcean(
  x: number,
  z: number,
  t: number,
  waves: readonly GerstnerWave[] = WAVES,
  amplitudeScale = 1,
): OceanSample {
  let height = 0;
  let dx = 0;
  let dz = 0;
  let nx = 0;
  let nz = 0;
  let ny = 1;
  for (const w of waves) {
    const a = w.amplitude * amplitudeScale;
    if (a === 0) continue;
    const k = (2 * Math.PI) / w.wavelength;
    const omega = k * w.speed;
    const theta = k * (w.dirX * x + w.dirZ * z) - omega * t;
    const s = Math.sin(theta);
    const c = Math.cos(theta);
    const q = w.steepness;
    height += a * s;
    dx += q * a * w.dirX * c;
    dz += q * a * w.dirZ * c;
    const wa = k * a;
    nx -= w.dirX * wa * c;
    nz -= w.dirZ * wa * c;
    ny -= q * wa * s;
  }
  const len = Math.hypot(nx, ny, nz) || 1;
  return { height, dx, dz, nx: nx / len, ny: ny / len, nz: nz / len };
}

/**
 * Height where the *displaced* surface sits above world XZ (x, z), found by two
 * fixed-point corrections of the horizontal displacement. Buoyancy wants this:
 * it asks "how high is the water under the hull here", not "where did the
 * point that started here go".
 */
export function oceanHeightAt(
  x: number,
  z: number,
  t: number,
  waves: readonly GerstnerWave[] = WAVES,
  amplitudeScale = 1,
): number {
  let px = x;
  let pz = z;
  for (let i = 0; i < 3; i++) {
    const s = sampleOcean(px, pz, t, waves, amplitudeScale);
    px = x - s.dx;
    pz = z - s.dz;
  }
  return sampleOcean(px, pz, t, waves, amplitudeScale).height;
}

/** True when the summed steepness cannot make the surface fold over itself. */
export function waveSetIsSafe(waves: readonly GerstnerWave[]): boolean {
  let sum = 0;
  for (const w of waves) sum += (w.steepness * w.amplitude * 2 * Math.PI) / w.wavelength;
  return sum < 1;
}

/** GLSL literal for one float, with a decimal point so it is never an int. */
function f(n: number): string {
  const s = n.toFixed(6);
  return s.includes('.') ? s : `${s}.0`;
}

/**
 * The wave set as a GLSL array initialiser plus its length define, so the
 * vertex shader is built from the same numbers the CPU reads.
 * Layout per wave: vec4(dirX, dirZ, amplitude, wavelength), vec2(speed, steepness).
 */
export function wavesToGlsl(waves: readonly GerstnerWave[]): string {
  const a = waves.map((w) => `vec4(${f(w.dirX)}, ${f(w.dirZ)}, ${f(w.amplitude)}, ${f(w.wavelength)})`);
  const b = waves.map((w) => `vec2(${f(w.speed)}, ${f(w.steepness)})`);
  return [
    `#define WAVE_COUNT ${waves.length}`,
    `const vec4 WAVE_A[WAVE_COUNT] = vec4[WAVE_COUNT](${a.join(', ')});`,
    `const vec2 WAVE_B[WAVE_COUNT] = vec2[WAVE_COUNT](${b.join(', ')});`,
  ].join('\n');
}

/**
 * Line-for-line TS port of the shader's `gerstner()` function, kept here so a
 * test can hold the two implementations against each other. The shader body
 * in `ocean-material.ts` is written to mirror this exactly.
 */
export function shaderPortSample(
  x: number,
  z: number,
  t: number,
  waves: readonly GerstnerWave[],
  amplitudeScale: number,
): OceanSample {
  let pos = { x: 0, y: 0, z: 0 };
  let nrm = { x: 0, y: 1, z: 0 };
  for (let i = 0; i < waves.length; i++) {
    const A = waves[i]!;
    const dirX = A.dirX;
    const dirZ = A.dirZ;
    const amp = A.amplitude * amplitudeScale;
    const k = 6.283185307179586 / A.wavelength;
    const w = k * A.speed;
    const q = A.steepness;
    const th = k * (dirX * x + dirZ * z) - w * t;
    const s = Math.sin(th);
    const c = Math.cos(th);
    pos = { x: pos.x + q * amp * dirX * c, y: pos.y + amp * s, z: pos.z + q * amp * dirZ * c };
    const wa = k * amp;
    nrm = { x: nrm.x - dirX * wa * c, y: nrm.y - q * wa * s, z: nrm.z - dirZ * wa * c };
  }
  const len = Math.hypot(nrm.x, nrm.y, nrm.z) || 1;
  return { height: pos.y, dx: pos.x, dz: pos.z, nx: nrm.x / len, ny: nrm.y / len, nz: nrm.z / len };
}
